import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { isFreshTimestamp, trackSignUpCompleted } from "@/lib/analytics";
import { apiJson } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import type { TokenPair } from "@/lib/auth-context";
import { safeNext } from "@/lib/safe-next";

// Not secret — this is sent to the browser to initialize Google Identity Services, the same way
// it'd appear in any network request anyway. Registered origins (not a redirect URI) are what
// actually gate this: see backend/app/auth/google_oauth.py's module docstring for why this
// replaced an earlier server-side-redirect implementation.
const GOOGLE_CLIENT_ID = "156487693862-12e25i9bkms975md3hnoejcvtnboiso2.apps.googleusercontent.com";
const GSI_SCRIPT_SRC = "https://accounts.google.com/gsi/client";

function loadGsiScript(onReady: () => void): () => void {
  if ((window as any).google?.accounts?.id) {
    onReady();
    return () => {};
  }
  const existing = document.querySelector(`script[src="${GSI_SCRIPT_SRC}"]`);
  if (existing) {
    existing.addEventListener("load", onReady);
    return () => existing.removeEventListener("load", onReady);
  }
  const script = document.createElement("script");
  script.src = GSI_SCRIPT_SRC;
  script.async = true;
  script.defer = true;
  script.onload = onReady;
  document.body.appendChild(script);
  return () => {};
}

// Renders Google's own Sign-In button via Google Identity Services (GSI). On success, Google
// hands us a signed ID token directly in-page (no navigation, no redirect_uri) — we POST it to
// the backend, which verifies it and returns our own session tokens.
export default function GoogleAuthButton({ next }: { next: string }) {
  const { establishSession } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(() => !!(window as any).google?.accounts?.id);
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const nextRef = useRef(next);
  nextRef.current = next;

  useEffect(() => loadGsiScript(() => setReady(true)), []);

  useEffect(() => {
    if (!node || !ready) return;
    const google = (window as any).google;
    if (!google?.accounts?.id) return;

    google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: async (resp: { credential: string }) => {
        setError(null);
        try {
          const tokens = await apiJson<TokenPair>("/auth/google/login", {
            method: "POST",
            body: JSON.stringify({ token: resp.credential }),
          });
          const me = await establishSession(tokens);
          // /auth/google/login creates the account on first sign-in and returns the same token
          // pair either way, so a just-minted created_at is what separates a real sign-up from a
          // returning user signing back in.
          if (isFreshTimestamp(me.created_at)) trackSignUpCompleted("google");
          navigate(safeNext(nextRef.current));
        } catch (err) {
          setError(err instanceof Error ? err.message : "Google sign-in failed");
        }
      },
    });
    node.innerHTML = "";
    google.accounts.id.renderButton(node, {
      theme: "filled_black",
      size: "large",
      width: 336,
      shape: "pill",
      text: "continue_with",
    });
  }, [node, ready, establishSession, navigate]);

  return (
    <div className="mb-5">
      <div ref={setNode} style={{ display: "flex", justifyContent: "center", minHeight: 40 }} />
      {error && <p className="mt-2 text-center text-sm text-red-400">{error}</p>}
    </div>
  );
}

export function OrDivider() {
  return (
    <div className="mb-5 flex items-center gap-3 text-xs" style={{ color: "var(--text-muted)" }}>
      <div className="h-px flex-1" style={{ background: "var(--border)" }} />
      or
      <div className="h-px flex-1" style={{ background: "var(--border)" }} />
    </div>
  );
}
